import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PHIAccessModal from "../components/audit/PHIAccessModal";
import "../assets/css/login.css";

const Login = () => {
  const { login, isAuthenticated } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPHIModal, setShowPHIModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Handle URL messages (like password change confirmations)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get("message");

    if (message === "password_changed") {
      setError(""); // Clear any existing errors
      // You could show a success message here if desired
    }
  }, [location]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/crm");
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      console.log("Starting login process...");
      console.log("Login parameters:", { email, hasPassword: !!password });

      // Call login with separate parameters as your AuthContext expects
      const result = await login(email, password);

      console.log("Login result:", result);

      if (result?.requiresPHIAcknowledgment || result?.requiresPHI) {
        console.log("PHI acknowledgement required");
        setPendingUser(result.user);
        setShowPHIModal(true);
        setIsLoading(false);
        return;
      }

      if (result?.success) {
        console.log("Login successful, redirecting...");
        navigate("/crm");
      } else {
        throw new Error(result?.error || "Login failed");
      }
    } catch (err) {
      console.error("Login failed:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });

      // Handle specific error cases
      let errorMessage = "Login failed. Please try again.";

      if (err.response?.status === 400) {
        errorMessage =
          err.response?.data?.error || "Invalid login credentials.";
      } else if (err.response?.status === 401) {
        errorMessage = "Invalid email or password.";
      } else if (err.response?.status === 423) {
        errorMessage = "Account is locked. Please contact administrator.";
      } else if (err.response?.status === 429) {
        errorMessage = "Too many login attempts. Please try again later.";
      } else if (err.code === "ECONNABORTED" || err.code === "ERR_NETWORK") {
        errorMessage = "Network error. Please check your connection.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePHIAcknowledgment = async () => {
    console.log("Processing PHI acknowledgement...");
    setIsLoading(true);

    try {
      // Complete the login process with PHI acknowledgement
      const result = await login(email, password, true); // Pass PHI acknowledgement flag

      if (result?.success) {
        console.log("PHI acknowledged, login complete");
        setShowPHIModal(false);
        setPendingUser(null);
        navigate("/crm"); // Changed from /dashboard to /crm to match your main login
      } else {
        throw new Error(result?.error || "PHI acknowledgement failed");
      }
    } catch (error) {
      console.error("PHI acknowledgement error:", error);
      setError("Failed to complete PHI acknowledgement. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePHICancel = () => {
    console.log("PHI acknowledgement cancelled - logging out");

    // Clear any partial authentication state
    setPendingUser(null);
    setShowPHIModal(false);

    // Show message
    setError("PHI acknowledgement is required to access the system.");

    // Could also call logout() here if needed
  };

  // Show PHI modal if needed
  if (showPHIModal && pendingUser) {
    return (
      <div className="login-container d-flex justify-content-center align-items-center">
        <PHIAccessModal
          onConfirm={handlePHIAcknowledgment}
          onCancel={handlePHICancel}
          entityType="System Access"
          entityId="universal-access"
          user={pendingUser}
        />
      </div>
    );
  }

  return (
    <div className="login-container d-flex justify-content-center align-items-center">
      <div className="card shadow p-4" style={{ width: "400px" }}>
        <h2 className="text-center mb-4">Login</h2>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              disabled={isLoading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
                disabled={isLoading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {showPHIModal ? "Completing Setup..." : "Signing In..."}
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div className="text-center mt-3">
          <Link
            to="/reset-password"
            className="text-decoration-none small"
            style={{
              opacity: isLoading ? 0.5 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            Forgot your password?
          </Link>
        </div>

        <div className="text-center mt-3 small text-muted">
          <p>
            <i className="bi bi-shield-lock me-1"></i>
            HIPAA Compliant System - All access is logged and monitored
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
